<template>
  <DashboardContent>
    <DashboardPageLayout title="Grade System">
      <div id="tour-grade-page" class="p-4" v-if="canView">
        <div id="tour-grade-actions" class="flex justify-end mb-4 gap-2" v-if="canCreate">
          <Button size="small" label="Quick Setup" icon="pi pi-bolt" severity="secondary" @click="openQuickSetup" data-testid="btn-grade-quick-setup" />
          <Button size="small" label="Add New Grade" icon="pi pi-plus" @click="openNew" data-testid="btn-grade-add-new" />
        </div>
        <div id="tour-grade-list">
          <GradeList :grade-groups="gradeGroups" :loading="loading" @edit="openEdit" @delete="handleDelete" />
        </div>

        <GradeFormDialog v-model:visible="gradeDialog" :grade-to-edit="gradeToEdit" :existing-groups="existingGroups"
          :loading="createLoading || updateLoading" :serverErrors="serverErrors" @save="handleSave" />

        <QuickGradeSetupDialog v-model:visible="quickSetupDialog" @create-batch="handleQuickBatchCreate" />

        <ConfirmDialog />
      </div>
      <div v-else class="flex flex-col items-center justify-center p-12 text-center text-gray-500">
        <i class="pi pi-lock text-4xl mb-4"></i>
        <p class="text-lg font-medium">Access Denied</p>
        <p>You do not have permission to view grade systems.</p>
      </div>
    </DashboardPageLayout>
  </DashboardContent>
</template>

<script setup lang="ts">
import DashboardPageLayout from '@/components/DashBoard/common/DashboardPageLayout.vue';
import DashboardContent from '@/components/DashBoard/common/DashboardContent.vue';
import GradeList from '@/components/DashBoard/GradeSystem/GradeList.vue';
import GradeFormDialog from '@/components/DashBoard/GradeSystem/GradeFormDialog.vue';
import QuickGradeSetupDialog from '@/components/DashBoard/GradeSystem/QuickGradeSetupDialog.vue';
import { useGradeSystem } from '@/composables/useGradeSystem';
import { useFormErrors } from '@/composables/useFormErrors';
import { useToast } from 'primevue/usetoast';
import type { Grade, SchoolGradeInput } from '@/types/composition-functions';
import { useUserStore } from '@/store/userStore';
import { storeToRefs } from 'pinia';
import { can, AppPermissions } from '@/utils/permissions';

const toast = useToast();
const { serverErrors, handleServerErrors, clearErrors } = useFormErrors();
const userStore = useUserStore();
const { currentUser } = storeToRefs(userStore);

const {
  gradeGroups,
  loading,
  createGrade,
  createLoading,
  createGradeBulk,
  updateGrade,
  updateLoading,
  deleteGrade,
} = useGradeSystem();

const existingGroups = computed(() => gradeGroups.value.map(g => g.name));

const gradeDialog = ref(false);
const quickSetupDialog = ref(false);
const gradeToEdit = ref<Grade | null>(null);

const canView = computed(() => can(currentUser.value, AppPermissions.GradeSystem.Actions.View, AppPermissions.GradeSystem.Resource));
const canCreate = computed(() => can(currentUser.value, AppPermissions.GradeSystem.Actions.Create, AppPermissions.GradeSystem.Resource));

const openNew = () => {
  clearErrors();
  gradeToEdit.value = null;
  gradeDialog.value = true;
};

const openQuickSetup = () => {
  quickSetupDialog.value = true;
};

const openEdit = (grade: Grade) => {
  clearErrors();
  gradeToEdit.value = grade;
  gradeDialog.value = true;
};

const handleDelete = async(id?: string | null) => {
  if (id) {
    await deleteGrade(id);
  }
};

const handleSave = async(input: SchoolGradeInput) => {
  let success = false;
  clearErrors();
  try {
    if (gradeToEdit.value?.id) {
      success = await updateGrade(gradeToEdit.value.id, input);
    } else {
      success = await createGrade(input);
    }

    if (success) {
      gradeDialog.value = false;
      gradeToEdit.value = null;
    }
  } catch (error) {
    handleServerErrors(error);
    if (Object.keys(serverErrors.value).length === 0) {
      toast.add({ severity: 'error', summary: 'Error', detail: error instanceof Error ? error.message : 'An error occurred', life: 3000 });
    }
  }
};

const handleQuickBatchCreate = async(grades: SchoolGradeInput[]) => {
  try {
    await createGradeBulk(grades);
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Error', detail: error instanceof Error ? error.message : 'An error occurred during batch creation', life: 3000 });
  }
};
</script>
